import { useMemo, type CSSProperties } from "react";
import {
  LegendOrdinal as VisxLegendOrdinal,
  type LegendOrdinalProps,
} from "@visx/legend";
import { scaleOrdinal, type PickD3Scale } from "@visx/scale";
import { Box } from "../../components/ui/box";
import { Text } from "../../components/ui/text";
import { useChartColors } from "./colors";

/**
 * Legend components (verbatim `@visx/legend`, except the themed `LegendOrdinal`
 * below). They render HTML, so feed them a scale: pair `LegendOrdinal` with
 * {@link useOrdinalColorScale} so swatch colors match the chart's themed series
 * ramp.
 */
export {
  Legend,
  LegendLinear,
  LegendQuantile,
  LegendThreshold,
  LegendSize,
  LegendItem,
  LegendLabel,
} from "@visx/legend";
import type { Omittable } from "../../lib/props";

 
type AnyOrdinalScale = PickD3Scale<"ordinal", any, any>;

/**
 * The themed ordinal legend every chart uses. Wraps visx `LegendOrdinal` with
 * house rules so legends read the same at any card size:
 *  - each label stays on ONE line (`whiteSpace: nowrap`, inherited by the label
 *    divs) — "Residential Roof" never breaks into "Residential" / "Roof" — while
 *    the row still wraps whole items onto a new line when it runs out of width;
 *  - the type sits a step below body copy (with swatches scaled to match) so a
 *    narrow card's legend doesn't crowd the chart;
 *  - labels take an EXPLICIT theme foreground color. visx renders plain HTML that
 *    inherits the ambient CSS `color`, which is a light card token in one place
 *    and near-black in another (e.g. the detail dialog) — pinning it keeps the
 *    legend readable on every surface, light or dark.
 * Callers keep the full visx prop surface (`scale`, `direction`, `itemMargin`,
 * `shapeMargin`, …); any container `style` they pass still wins.
 */
export function LegendOrdinal<Scale extends AnyOrdinalScale>({
  style,
  shapeWidth = 11,
  shapeHeight = 11,
  ...props
}: LegendOrdinalProps<Scale>) {
  const colors = useChartColors();
  const containerStyle: CSSProperties = {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    whiteSpace: "nowrap",
    fontSize: 11,
    lineHeight: 1.3,
    color: colors.foreground,
    ...style,
  };
  return (
    <VisxLegendOrdinal
      {...(props as LegendOrdinalProps<Scale>)}
      shapeWidth={shapeWidth}
      shapeHeight={shapeHeight}
      style={containerStyle}
    />
  );
}

/**
 * An ordinal scale mapping series labels → the themed chart ramp, for
 * `<LegendOrdinal scale={useOrdinalColorScale(labels)} />`. Same coloring rule
 * XYChart applies to series by `dataKey`, so the legend stays in sync.
 */
export function useOrdinalColorScale(domain: string[]) {
  const c = useChartColors();
  return useMemo(
    () => scaleOrdinal<string, string>({ domain, range: c.ramp }),
    [domain, c],
  );
}

export type ChartRampLegendSwatch =
  | string
  | {
      color: string;
      opacity?: number;
    };

export type ChartRampLegendProps = Omittable<{
  /** Swatches in display order, already resolved to CSS colors by the caller's scale. */
  swatches: ChartRampLegendSwatch[];
  startLabel?: string;
  endLabel?: string;
  /** Swatch size in px. */
  size?: number;
}>;

/**
 * Compact sequential ramp legend for custom chart primitives. It accepts resolved
 * colors so callers can sample the exact active encoding, while keeping swatch
 * rendering inside the chart kit instead of consumer apps.
 */
export function ChartRampLegend({
  swatches,
  startLabel,
  endLabel,
  size = 10,
}: ChartRampLegendProps) {
  if (swatches.length === 0) return null;

  return (
    <Box direction="row" align="center" justify="end" gap="2xs">
      {startLabel ? (
        <Text size="xs" variant="muted" truncate>
          {startLabel}
        </Text>
      ) : null}
      <Box direction="row" align="center" gap="2xs">
        {swatches.map((swatch, i) => {
          const color = typeof swatch === "string" ? swatch : swatch.color;
          const opacity = typeof swatch === "string" ? undefined : swatch.opacity;
          return (
            // A plain colored square: the value is an already-resolved rgb from
            // the caller's scale, so inline backgroundColor is the sanctioned
            // JS-side color path (like SVG fill). Size is dynamic px.
            <Box rounded="sm"
              key={`${color}-${i}`}
              width={size}
              height={size}
              style={{
                backgroundColor: color,
                ...(opacity == null ? null : { opacity }),
              }}
            />
          );
        })}
      </Box>
      {endLabel ? (
        <Text size="xs" variant="muted" truncate>
          {endLabel}
        </Text>
      ) : null}
    </Box>
  );
}
